import { Injectable } from '@angular/core';
import { SQLite, SQLiteObject } from '@awesome-cordova-plugins/sqlite/ngx';
import { Platform } from '@ionic/angular';
import { BehaviorSubject, catchError, forkJoin, from, map, Observable, of, throwError } from 'rxjs';
import { PlatformService } from '../platform/platform.service';
import { Storage } from '@ionic/storage-angular';
import { Logger, LoggingService } from 'ionic-logging-service';
import { StorageMap } from '@ngx-pwa/local-storage';

@Injectable({
  providedIn: 'root'
})
export class StorageService {

  private db: SQLiteObject = null;
  private dbReady: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private dataStream = new BehaviorSubject([]);
  private isCordova: boolean = this.platformService.isCordova();
  private logger: Logger;
  private mainDBName = 'data.mainDB';
  private tableNameKeyValue = 'printixapp_key_value';
  private prefix = 'ls.';

  constructor(
    protected platform: Platform,
    private platformService: PlatformService,
    protected SQLite: SQLite,
    private pwaStorage: StorageMap,
    private loggingService: LoggingService,
    private storage: Storage
  ) {
    this.logger = this.loggingService.getLogger("[StorageService]");
    const methodName = "ctor";
    this.logger.entry(methodName);
    this.init();
  }

  private init() {
    this.platform.ready().then(() => {
      if (this.isCordova) {
        this.createDB().then((SQLiteObject: SQLiteObject) => {
          if (SQLiteObject) {
            this.db = SQLiteObject;
            this.createTable().subscribe({
              error: (error: any) => {
                this.logger.info('createTable() failed – falling back to localStorage: ' + JSON.stringify(error));
                this.db = null;
                this.isCordova = false;
                this.dbReady.next(true);
              }
            });
          } else {
            // SQLite creation failed (e.g. running on simulator without native SQLite support).
            // Fall back to PWA/localStorage storage so the app doesn't stall on a white screen.
            this.logger.info('SQLite unavailable – falling back to localStorage (simulator mode)');
            this.isCordova = false;
            this.dbReady.next(true);
          }
        });
      }
    });
  }

  getDatabaseState() {
    return this.dbReady.asObservable();
  }

  private prefixKey(key: string): string {
    return `${this.prefix}${key}`;
  }

  private createDB(): Promise<void | SQLiteObject> {
    this.logger.info('CREATE_DB');
    return this.SQLite.create({
      name: this.mainDBName,
      location: 'default'
    }).then((SQLiteObject: SQLiteObject) => {
      return SQLiteObject;
    }, (error: any) => {
      this.logger.info('SQLite.create() ERROR: ' + JSON.stringify(error));
    });
  }

  private createTable(): Observable<void> {
    this.logger.info('CREATE_TABLE');
    const query: string = 'CREATE TABLE IF NOT EXISTS ' + this.tableNameKeyValue +
      ' (key TEXT PRIMARY KEY, value TEXT)';
    return from(this.db.executeSql(query, [])).pipe(
      map(() => {
        this.dbReady.next(true);
        return;
      }),
      catchError((error: any) => {
        this.logger.info('CREATE_TABLE() - ERROR: ' + JSON.stringify(error));
        return throwError(error);
      })
    );
  }

  public addItemToLocalStorage(key: string, value?: any, fromMethod?: string): Observable<void> {
      const source$ = this.isCordova
        ? this.addItemToLocalStorage_native(key, value, fromMethod)
        : this.addItemToLocalStorage_pwa(key, value, fromMethod);

      return source$.pipe(
        catchError((error: any) => {
          this.logErrorMessage(error, 'addItemToLocalStorage() – swallowed for key: ' + key + ' fromMethod: ' + (fromMethod || 'unknown'));
          return of(undefined as void);
        })
      );
  }

  private addItemToLocalStorage_native(key: string, value?: any, fromMethod?: string): Observable<void> {
    this.logger.info('addItemToLocalStorage_native: ' + 'key: ' + key + ' fromMethod: ' + fromMethod);
    value = JSON.stringify(value);
    const query: string = 'INSERT OR REPLACE INTO ' + this.tableNameKeyValue + ' (key, value) VALUES (?, ?)';
    const values: Array<any> = [key, value];
    return this.executeQuery(query, values).pipe(
      map(() => {
        return; // Emit void
      }),
      catchError((error: any) => {
        this.logErrorMessage(error, 'addItemToLocalStorage_native(), executeQuery');
        return throwError(error);
      })
    );
  }

  private addItemToLocalStorage_pwa(key: string, value: any, fromMethod?: string): Observable<void> {
    return new Observable((observer) => {
        try {
          // Use localStorage.setItem() for auth tokens
          const prefixedKey = this.prefixKey(key);
          const stringValue = JSON.stringify(value);

          localStorage.setItem(prefixedKey, stringValue);
          observer.next(); // Emit void
          observer.complete();
        } catch (error) {
          this.logErrorMessage(error, 'addItemToLocalStorage_pwa(), localStorage.setItem');
          // localStorage failed – fall back to pwaStorage before erroring
          this.pwaStorage.set(this.prefixKey(key), value).subscribe({
            next: () => {
              observer.next(); // Emit void
              observer.complete();
            },
            error: (pwaError: any) => {
              this.logErrorMessage(pwaError, 'addItemToLocalStorage_pwa(), pwaStorage.set');
              observer.error(pwaError); // Both storage mechanisms failed
            }
          });
          return;
        }
    });
  }

  public getItemFromLocalStorage(key: string, fromMethod?: string): Observable<any> {
    this.logger.info('getItemFromLocalStorage()' + 'key: ' + key + ' fromMethod: ' + fromMethod);
    if (this.isCordova) { // get stored items for devices
      return this.getItemFromLocalStorage_native(key, fromMethod);
    } else { // get stored items for browser
      return this.getItemFromLocalStorage_pwa(key);
    }
  }

  private getItemFromLocalStorage_native(key: string, fromMethod?: string): Observable<any> {
    const query: string = 'SELECT value FROM ' + this.tableNameKeyValue + ' WHERE key=?';
    const values: Array<string> = [key];
    return this.executeQuery(query, values, fromMethod).pipe(
      map((storedItem: any) => {
        return storedItem;
      }),
      catchError((error: any) => {
        this.logErrorMessage(error, 'getItemFromLocalStorage_native(), executeQuery');
        return of(undefined); // Return a safe value or handle the error appropriately
      })
    );
  }

  private getItemFromLocalStorage_pwa(key: string): Observable<any> {
    return new Observable((observer) => {
      try {
        // Attempt to get the item from localStorage
        const prefixedKey = this.prefixKey(key);
        const localStorageValue = localStorage.getItem(prefixedKey);
        const parsedValue = localStorageValue ? JSON.parse(localStorageValue) : null;
        observer.next(parsedValue); // Emit the value from localStorage
        observer.complete();
      } catch (error) {
        this.logErrorMessage(error, 'getItemFromLocalStorage_pwa(), localStorage.getItem');
        // If not found in localStorage, fallback to pwaStorage
        this.pwaStorage.get(this.prefixKey(key)).subscribe({
          next: (storedString: string | undefined) => {
            observer.next(storedString); // Emit the value from pwaStorage
            observer.complete();
          },
          error: (error: any) => {
            this.logErrorMessage(error, 'getItemFromLocalStorage_pwa(), pwaStorage.get');
            observer.error(error); // Emit the error
          }
        });
      }
    });
  }

  protected executeQuery(query: string, values: Array<any>, fromMethod?: string): Observable<any> {
    return new Observable((observer) => {
      if (!this.db) {
        this.logErrorMessage('db is null/not initialized', 'executeQuery() from ' + (fromMethod || 'unknown'));
        observer.error(new Error('SQLite database is not initialized'));
        return;
      }
      this.db.executeSql(query, values).then((data: any) => {
        try {
          if (data.rows.length > 0) {
            if (data.rows.length === 1) {
              observer.next(JSON.parse(data.rows.item(0).value));
              observer.complete();
            } else {
              const listToReturn: Array<any> = [];
              for (let i = 0; i < data.rows.length; i++) {
                listToReturn.push(JSON.parse(data.rows.item(i).value));
              }
              observer.next(listToReturn);
              observer.complete();
            }
          } else {
            observer.next(null);
            observer.complete();
          }
        } catch (parseError: any) {
          this.logErrorMessage(parseError, 'executeQuery(), JSON.parse failed for query from ' + (fromMethod || 'unknown'));
          observer.error(parseError);
        }
      }, (error: any) => {
        this.logErrorMessage(error, 'executeQuery(), executeSql from ' + (fromMethod || 'unknown'));
        observer.error(error);
      });
    });
  }

  public removeStoredItems(keys: string[]): Observable<void> {
    if (this.isCordova) {
      return this.removeMultipleItems_native(keys);
    } else {
      return this.removeMultipleItems_pwa(keys);
    }
  }

  private removeMultipleItems_native(keys: string[]): Observable<void> {
    const deleteObservables = keys.map(key => {
      const query = 'DELETE FROM ' + this.tableNameKeyValue + ' WHERE key=?';
      return this.executeQuery(query, [key]).pipe(
        map(() => void 0)
      );
    });
    return forkJoin(deleteObservables).pipe(map(() => void 0));
  }

  private removeMultipleItems_pwa(keys: string[]): Observable<void> {
    keys.forEach(key => {
      localStorage.removeItem(this.prefixKey(key));
    });
    const deleteObservables = keys.map(key => this.pwaStorage.delete(this.prefixKey(key)));
    return forkJoin(deleteObservables).pipe(map(() => void 0));
  }

  private logErrorMessage(error: any, calledFunction: string): void {
    this.logger.info('logErrorMessage()' + calledFunction + ' ERROR: ' + JSON.stringify(error));
  }
}
